import type { XrefIntakes, XrefPumps } from "@/composables/forms/formTypes";
import {
  DEFAULT_ESP_PUMP_RPM,
  DEFAULT_HPS_PUMP_RPM,
  PUMP_EFFICIENCY
} from "@/libs/calculations/constants";

export function pump_specific_speed(
  bep_flow: number,
  bep_head: number
): number {
  return (3500 * Math.sqrt(bep_flow / 34.296)) / Math.pow(bep_head, 0.75);
}

export function pump_gas_lock_ratio(pump_specific_speed: number): number {
  return (
    0.0355943298 +
    0.0000869206 * pump_specific_speed +
    0.0000000002 * pump_specific_speed * pump_specific_speed
  );
}

export function getTemperatureDeltaForPumpStage(
  generatedPressureHeadFt: number,
  liquidSpecificHeatCapacity: number,
  pumpEfficiencyRatio: number
) {
  if (liquidSpecificHeatCapacity === 0 || pumpEfficiencyRatio === 0) {
    return 0;
  }
  return (
    (generatedPressureHeadFt * (1 - pumpEfficiencyRatio)) /
    (778 * liquidSpecificHeatCapacity * pumpEfficiencyRatio)
  );
}

// Pump IntOil Specific Gravity S_oil SpG
// Water Specific Gravity S_water SpG
// Gas Specific Gravity S_gas SpG
// Intake Temperature T_i fahrenheit
// Discharge Temperature T_d fahrenheit
// Water Cut WC %
// Reservoir Pressure P_r psia
// Bubble Point Pressure P_b psia
// Wellhead Temperature wellHT fahrenheit
// Surface Tubing Pressure surfaceTP psia
// Gas/Oil Ratio GOR scf/stb
// Solution GOR Correlation corr_solugor
// Oil Volume Factor Correlation corr_oil_vol
// Water Volume Factor Correlation corr_water_vol
// Oil Density Correlation corr_oil_density
// HB Adjustment hb_adjustment %
// ORK Adjustment ork_adjustment %
// Is User Entered Bubble Point Pressure user_P_b
// returns TDH headft
export function getMultiphaseTotalDynamicHeadFt(
  pressurePSIA: number,
  liquidDensityLBMFT3: number
): number {
  const generatedPressureHeadFt =
    (144.144 * pressurePSIA) / liquidDensityLBMFT3;

  return generatedPressureHeadFt;
}

export function getNominalPressureHeadFt(
  q: number,
  h0: number,
  h1: number,
  h2: number,
  h3: number,
  h4: number,
  h5: number
): number {
  const h_nominal =
    h0 +
    h1 * q +
    h2 * q * q +
    h3 * q * q * q +
    h4 * q * q * q * q +
    h5 * q * q * q * q * q;
  return h_nominal;
}

export function getNominalPowerBrakeHP(
  q: number,
  b0: number,
  b1: number,
  b2: number,
  b3: number,
  b4: number,
  b5: number
): number {
  const bhp =
    b0 +
    b1 * q +
    b2 * q * q +
    b3 * q * q * q +
    b4 * q * q * q * q +
    b5 * q * q * q * q * q;
  return bhp;
}

// Average Flow flowRateSTBD
// Produced TDH totalDynamicHeadFt
// HP Required fluidDensitySpGr
// Brake HP breakHp
// returns efficiency %
export function getPumpEfficiencyRatio(
  flowRateSTBD: number,
  totalDynamicHeadFt: number,
  fluidDensitySpGr: number,
  breakHp: number
): number {
  return (
    (flowRateSTBD * totalDynamicHeadFt * fluidDensitySpGr) /
    (breakHp * PUMP_EFFICIENCY)
  );
}

export function getPumpHeadGasDerate(gasBeforeIntakeRatio: number): number {
  return gasBeforeIntakeRatio < 0.15
    ? 1
    : 1 -
        0.2812025767 * gasBeforeIntakeRatio -
        0.0676915031 * gasBeforeIntakeRatio ** 2;
}

export function calculateSealOperatingHP(
  sealThrustCollarODIn: number,
  sealThrustCollarIDIn: number,
  sealBearingRatingLBS: number,
  sealViscosityCp: number,
  operationalThrustLoadLBS: number,
  frequencyRatio: number,
  isScenarioEspShore: boolean
): number {
  const sealBearingRatingPSIA =
    sealBearingRatingLBS /
    (Math.PI * (sealThrustCollarODIn / 2) ** 2 -
      Math.PI * (sealThrustCollarIDIn / 2) ** 2);
  const operationalThrustLoadPSIA =
    operationalThrustLoadLBS /
    (Math.PI * (sealThrustCollarODIn / 2) ** 2 -
      Math.PI * (sealThrustCollarIDIn / 2) ** 2);

  // We do not pass in the actual rpm here since this function is used
  // inside of processPumpCalculations where it would cause a circular
  // dependency to pass in the rpm.
  const rpmEstimate =
    frequencyRatio *
    (isScenarioEspShore ? DEFAULT_ESP_PUMP_RPM : DEFAULT_HPS_PUMP_RPM);

  const filmThicknessIn =
    0.0055 * Math.sqrt(sealBearingRatingPSIA / operationalThrustLoadPSIA);

  const sealOperatingHP =
    (0.000000000000396 *
      sealViscosityCp *
      rpmEstimate ** 2 *
      (sealThrustCollarODIn ** 4 - sealThrustCollarIDIn ** 4)) /
      filmThicknessIn || 0;
  return sealOperatingHP;
}

export function calculateTotalIntakeOperatingHP(
  xrefIntakes: XrefIntakes,
  flowRateBBLD: number,
  fluidSpecificGravity: number,
  frequencyRatio: number
): number {
  const totalHP = xrefIntakes.reduce((totalHP, xrefIntake) => {
    return (
      totalHP +
      calculateIntakeOperatingHP(
        xrefIntake,
        flowRateBBLD,
        fluidSpecificGravity,
        frequencyRatio
      )
    );
  }, 0);
  return totalHP;
}

export function calculateIntakeOperatingHP(
  xrefIntake: XrefIntakes[number],
  flowRateBBLD: number,
  fluidSpecificGravity: number,
  frequencyRatio: number
): number {
  const bhp0 = xrefIntake.r_intake?.bhp0 || 0;
  const bhp1 = xrefIntake.r_intake?.bhp1 || 0;
  const bhp2 = xrefIntake.r_intake?.bhp2 || 0;
  const bhp3 = xrefIntake.r_intake?.bhp3 || 0;
  const bhp4 = xrefIntake.r_intake?.bhp4 || 0;
  const bhp5 = xrefIntake.r_intake?.bhp5 || 0;
  const numStages = xrefIntake.r_intake?.stage_count || 0;
  const intakeHp =
    (bhp0 +
      bhp1 * flowRateBBLD +
      bhp2 * flowRateBBLD ** 2 +
      bhp3 * flowRateBBLD ** 3 +
      bhp4 * flowRateBBLD ** 4 +
      bhp5 * flowRateBBLD ** 5) *
    frequencyRatio ** 3 *
    fluidSpecificGravity *
    numStages;
  return intakeHp;
}
export function getOperatingThrustLoad(
  xrefPump: XrefPumps[number],
  dischargePressurePSIA = 0,
  pumpIntakePressurePSIA: number,
  fluidDensityInletLBPFT3 = 0,
  fluidDensityOutletLBPFT3 = 0,
  hasCompressionPump: boolean,
  rpm: number,
  isScenarioEspShore = true
): number {
  const pump = xrefPump.r_pump;

  if (!pump) {
    return 0;
  }

  const pressure_difference = dischargePressurePSIA - pumpIntakePressurePSIA;

  const shaftRadius = pump.shaft_diameter_inches / 2;

  if (hasCompressionPump) {
    const shut_in_rpm_ratio =
      rpm / (isScenarioEspShore ? DEFAULT_ESP_PUMP_RPM : DEFAULT_HPS_PUMP_RPM);

    const avg_specific_gravity =
      (fluidDensityInletLBPFT3 + fluidDensityOutletLBPFT3) / 2 / 62.428;

    const stage_count = xrefPump.total_stages || 1;
    return (
      pump.compression_thrust_per_stage *
      Math.pow(shut_in_rpm_ratio, 2) *
      avg_specific_gravity *
      stage_count
    );
  } else {
    return Math.PI * Math.pow(shaftRadius, 2) * pressure_difference;
  }
}
